// S3 — Post-combat "Other fights" scoreboard. Reads `roundResults`
// from the match store and renders the AI-vs-AI silent fights for the
// most-recently completed round. Collapsible: default open on desktop,
// closed on mobile (toggled by a header chip).
//
// Shape (from `src/game/lobby.ts`):
//   interface PairingOutcome {
//     round: number;
//     aId: string;
//     bId: string;            // === aId for ghost fights
//     winner: 'a' | 'b' | 'draw';
//     damage: number;
//     involvesHuman: boolean; // we filter these out — they're the main fight
//   }
//
// Opponents and their hero are looked up from the store's `opponents`
// array (AIPlayer[]). The human is identified by HUMAN_PLAYER_ID ('P0')
// and never appears in this panel (their fight is the main result block).
//
// Visual: a tight ticker — two name chips per row, winner in sodium,
// loser in aerosolRed, draws in marker. Damage shown to the right.
// Monospace data font to feel like a scoreboard.

import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useMatchStore, HUMAN_PLAYER_ID } from '../stores/match';
import { allHeroes } from '../game/data/heroes';
import { getTribeDefinition } from '../game/data/tribes';
import { EDGES } from '../theme/tokens';
import type { PairingOutcome, AIPlayer } from '../game/lobby';

interface OtherFightsPanelProps {
  /** Round to display fights for. If omitted, defaults to the latest
      round present in `roundResults` — that's the round the user is
      currently sitting on after playback. */
  round?: number;
}

function hexFromColorInt(n: number): string {
  return `#${n.toString(16).padStart(6, '0')}`;
}

function initialsForName(name: string): string {
  const parts = name.split(/[\s-]+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/**
 * Build a quick lookup that maps a player id to a display object.
 * Pulls hero name + tribe color from `opponents` (AIPlayer[]); falls
 * back to a generic "AI {id}" if missing — defensive against the human
 * id slipping through.
 */
function buildPlayerLookup(opponents: AIPlayer[]): Record<
  string,
  { displayName: string; initials: string; color: string }
> {
  const out: Record<string, { displayName: string; initials: string; color: string }> = {};
  for (const o of opponents) {
    const hero = allHeroes.find((h) => h.id === o.heroId) ?? o.hero;
    const tribe = hero?.tribe ? getTribeDefinition(hero.tribe) : undefined;
    const color = tribe ? hexFromColorInt(tribe.color) : '#8a7e6c';
    const displayName = hero?.name ?? o.name ?? o.id;
    out[o.id] = {
      displayName,
      initials: initialsForName(displayName),
      color,
    };
  }
  return out;
}

export function OtherFightsPanel({ round }: OtherFightsPanelProps) {
  const roundResults = useMatchStore((s) => s.roundResults) as PairingOutcome[];
  const opponents = useMatchStore((s) => s.opponents) as AIPlayer[];

  // If no explicit round was passed, fall back to the latest round
  // present in roundResults. This handles both possible orderings:
  //   - Agent G's revision may move runRoundTick into endTurn (so
  //     fights for round R land in roundResults BEFORE the user sees
  //     the post-playback block).
  //   - The current store appends silentFights in applyCombatDamage
  //     (so the panel will be empty during this round but populate
  //     for the next round's preview — still useful as a recap).
  const latestRound = roundResults.length > 0
    ? roundResults.reduce((m, f) => (f.round > m ? f.round : m), 0)
    : 0;
  const resolvedRound = round ?? latestRound;

  // Default closed on mobile, open on desktop. We detect viewport
  // width once on mount and don't fight resize — the user can still
  // toggle either way.
  const [open, setOpen] = useState<boolean>(() => {
    if (typeof window === 'undefined') return true;
    return window.matchMedia('(min-width: 768px)').matches;
  });

  // Defensive: ensure roundResults is iterable even if Agent G's
  // store revision hasn't landed.
  const fights: PairingOutcome[] = Array.isArray(roundResults) ? roundResults : [];

  // Filter to AI-vs-AI fights for the resolved round. Excludes the
  // human's own fight (which is shown by the main winner overlay).
  const filtered = fights.filter(
    (f) =>
      f.round === resolvedRound &&
      !f.involvesHuman &&
      f.aId !== HUMAN_PLAYER_ID &&
      f.bId !== HUMAN_PLAYER_ID,
  );

  const lookup = buildPlayerLookup(opponents);

  // If there are no fights to show, render nothing — clean fallback
  // for round 1 corner cases or a 2-player remnant lobby.
  useEffect(() => {
    // intentional no-op — kept around in case we add focus-on-mount.
  }, []);

  if (filtered.length === 0) return null;

  return (
    <section
      className="rounded-md border bg-stucco1/80 px-md py-sm md:px-lg md:py-md"
      style={{ borderColor: EDGES.soft }}
      aria-label="other fights this round"
    >
      <header className="flex items-center justify-between">
        <h3 className="font-data text-[11px] uppercase tracking-[0.3em] text-marker md:text-[13px]">
          Block report · round {resolvedRound}
        </h3>
        <button
          onClick={() => setOpen((v) => !v)}
          className="font-data text-[10px] uppercase tracking-[0.2em] text-graphite hover:text-bone md:text-[11px]"
          aria-expanded={open}
          aria-controls="other-fights-list"
        >
          Other fights {open ? '⌃' : '⌄'}
        </button>
      </header>

      <AnimatePresence initial={false}>
        {open && (
          <motion.ul
            id="other-fights-list"
            key="list"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.18 }}
            className="mt-sm flex flex-col gap-xs overflow-hidden md:mt-md md:gap-sm"
          >
            {filtered.map((fight, i) => {
              const ghost = fight.aId === fight.bId;
              const a = lookup[fight.aId];
              const b = lookup[fight.bId];

              // If lookup misses (shouldn't), fall back to the raw id
              // so the row still tells a story.
              const aName = a?.displayName ?? fight.aId;
              const bName = b?.displayName ?? fight.bId;
              const aInitials = a?.initials ?? fight.aId.slice(-2);
              const bInitials = b?.initials ?? fight.bId.slice(-2);
              const aColor = a?.color ?? '#8a7e6c';
              const bColor = b?.color ?? '#8a7e6c';

              const aTone =
                fight.winner === 'a'
                  ? 'text-sodium'
                  : fight.winner === 'b'
                    ? 'text-aerosolRed'
                    : 'text-marker';
              const bTone =
                fight.winner === 'b'
                  ? 'text-sodium'
                  : fight.winner === 'a'
                    ? 'text-aerosolRed'
                    : 'text-marker';

              const verb =
                fight.winner === 'draw'
                  ? 'drew'
                  : fight.winner === 'a'
                    ? 'beat'
                    : 'lost to';

              return (
                <li
                  key={`${fight.round}-${i}-${fight.aId}-${fight.bId}`}
                  className="flex items-center justify-between gap-sm rounded-sm bg-stucco2/40 px-sm py-xs md:px-md md:py-sm"
                >
                  <div className="flex min-w-0 flex-1 items-center gap-xs">
                    <PlayerChip
                      initials={aInitials}
                      color={aColor}
                      faded={fight.winner === 'b'}
                    />
                    <span
                      className={`truncate font-data text-[11px] uppercase tracking-[0.15em] md:text-[12px] ${aTone}`}
                    >
                      {aName}
                    </span>
                  </div>

                  <span className="font-data text-[9px] uppercase tracking-[0.3em] text-graphite md:text-[10px]">
                    {ghost ? 'ghost' : verb}
                  </span>

                  <div className="flex min-w-0 flex-1 items-center justify-end gap-xs">
                    {!ghost && (
                      <>
                        <span
                          className={`truncate font-data text-[11px] uppercase tracking-[0.15em] md:text-[12px] ${bTone}`}
                        >
                          {bName}
                        </span>
                        <PlayerChip
                          initials={bInitials}
                          color={bColor}
                          faded={fight.winner === 'a'}
                        />
                      </>
                    )}
                  </div>

                  <span
                    className={`ml-sm shrink-0 font-data text-[10px] uppercase tracking-[0.2em] md:text-[11px] ${
                      fight.damage > 0 ? 'text-aerosolRed' : 'text-graphite'
                    }`}
                  >
                    {fight.damage > 0 ? `-${fight.damage}` : '—'}
                  </span>
                </li>
              );
            })}
          </motion.ul>
        )}
      </AnimatePresence>
    </section>
  );
}

function PlayerChip({
  initials,
  color,
  faded,
}: {
  initials: string;
  color: string;
  faded: boolean;
}) {
  return (
    <span
      className={`inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-sm font-data text-[10px] font-bold text-sharpie transition-opacity md:h-7 md:w-7 md:text-[11px] ${
        faded ? 'opacity-50 saturate-50' : ''
      }`}
      style={{ backgroundColor: color }}
      aria-hidden
    >
      {initials}
    </span>
  );
}
